import { Button, Css, multiFilter, singleFilter, useModal, usePersistedFilter } from "@homebound/beam";
import { useMemo } from "react";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { DesignPackageLocation } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { PageHeader } from "src/routes/layout/PageHeader";
import {
  ProjectFinishScheduleMetadataQuery,
  useProjectFinishScheduleMetadataQuery,
} from "src/routes/projects/finish-schedule/ProjectFinishSchedulePage.generated";
import { createProjectLotSummaryUrl } from "src/RouteUrls";
import { count, queryResult } from "src/utils";
import { ExportProjectFinishScheduleModal } from "./components/ExportProjectFinishScheduleModal";
import { ProjectFinishScheduleTable } from "./components/ProjectFinishScheduleTable";
import { useProjectLocationsTreeFilter } from "./useProjectLocationsTreeFilter";

export type ProjectFinishScheduleParams = { projectId: string };

export type ProjectFinishScheduleFilters = {
  costCodes: string[];
  locationInPath: string[];
  designPackageLocation: DesignPackageLocation;
};

export function ProjectFinishSchedulePage() {
  const { projectId } = useRouteParams<ProjectFinishScheduleParams>();
  const projectQuery = useProjectFinishScheduleMetadataQuery({ variables: { projectId } });
  return queryResult(projectQuery, ({ project }) => <ProjectFinishScheduleView project={project} />);
}

type ProjectFinishScheduleViewProps = { project: ProjectFinishScheduleMetadataQuery["project"] };

function ProjectFinishScheduleView(props: ProjectFinishScheduleViewProps) {
  const { project } = props;
  const { openModal } = useModal();
  const projectOptions = project.readyPlanConfig?.options.map((o) => o.readyPlanOption) ?? [];
  const designOptions = projectOptions
    .filter((rpo) => rpo.optionGroup.forDesignPackages)
    .sortBy((rpo) => rpo.optionGroup.order);
  // Breadcrumb href is empty since this is only being used as a title in the header
  const breadCrumb = [{ href: "", label: "Finish Schedule" }];

  const { filter, setFilter, filterDefs } = useFinishScheduleFilters(project.stage?.costCodes ?? [], project.id);

  return (
    <>
      <PageHeader
        xss={Css.mb0.$}
        title={project.name}
        breadcrumb={breadCrumb}
        subTitle={`${designOptions.map((rpo) => rpo.name).join(", ")} (${count(projectOptions.length, "option")})`}
        backButton={createProjectLotSummaryUrl(project.id)}
        right={
          <>
            <div css={Css.df.gap3.$}>
              {/* TODO: follow-up task to add DP link */}
              {/* <Button
                variant="text"
                label="Design Package"
                onClick={createDesignPackageUrl(designPackage.id, designPackage.version.id)}
                icon="linkExternal"
              /> */}
              <Button
                label="Export"
                onClick={() => openModal({ content: <ExportProjectFinishScheduleModal project={project} /> })}
              />
            </div>
          </>
        }
      />
      <ProjectFinishScheduleTable filter={filter} setFilter={setFilter} filterDefs={filterDefs} />
    </>
  );
}

function useFinishScheduleFilters(costCodes: NamedFragment[], projectId: string) {
  const locationsTreeFilter = useProjectLocationsTreeFilter(projectId, { isProduct: true });

  const filterDefs = useMemo(() => {
    return {
      costCodes: multiFilter({
        label: "Cost Code",
        options: costCodes?.sortByKey("name") ?? [],
        getOptionLabel: ({ name }) => name,
        getOptionValue: ({ id }) => id,
        sizeToContent: true,
      }),
      locationInPath: locationsTreeFilter,
      designPackageLocation: singleFilter({
        label: "Design Package",
        options: [
          { code: DesignPackageLocation.Interior, name: "Interior" },
          { code: DesignPackageLocation.Exterior, name: "Exterior" },
        ],
        getOptionValue: ({ code }) => code,
        getOptionLabel: ({ name }) => name,
      }),
    };
  }, [locationsTreeFilter, costCodes]);

  const { filter, setFilter } = usePersistedFilter({
    filterDefs,
    storageKey: `project-finish-schedule-filter-${projectId}`,
  });

  return { filter, setFilter, filterDefs };
}
